/**
 * profile-svc /profile/me/photo — the caller's own profile photo (V068).
 *
 * Self-only, works for any role (client, agent, brokerage_admin,
 * super_admin) — shared by the admin app's My Profile page and the client
 * web app's profile page, since both already call GET/PATCH /profile/me on
 * this same service. Same two-legged presigned upload shape as every other
 * photo/logo/doc upload in the platform: presign → PUT straight to S3 →
 * confirm registers the key.
 */

import type { ApiClient } from "../client";

export interface ProfilePhotoUploadUrlResponse {
  upload_url: string;
  content_type: string;
  s3_key: string;
}

export const profilePhotoService = {
  presignUpload(client: ApiClient, ext: string) {
    return client.request<ProfilePhotoUploadUrlResponse>(
      "/profile/me/photo/presign",
      { method: "POST", body: { ext } },
    );
  },

  /** Returns the updated profile (photo_url now set) — callers can use the
   * response directly instead of a separate refetch. */
  confirmUpload<TProfile>(client: ApiClient, s3Key: string) {
    return client.request<TProfile>("/profile/me/photo/confirm", {
      method: "POST",
      body: { s3_key: s3Key },
    });
  },
};

/**
 * PUT the photo file straight to S3. `contentType` must match exactly what
 * `presignUpload` returned — it is part of the presigned signature.
 */
export async function uploadProfilePhotoFile(
  uploadUrl: string,
  contentType: string,
  file: Blob,
): Promise<void> {
  const response = await fetch(uploadUrl, {
    method: "PUT",
    headers: { "Content-Type": contentType },
    body: file,
  });
  if (!response.ok) {
    throw new Error(`Photo upload failed (${response.status})`);
  }
}
